import { useTranslation } from "react-i18next";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import {
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
} from "@mui/material";

import MaterialButton from "../button";
import { CustomizedText } from "../styled-componnets";
import "./bottom-bar.css";
import {
	ButtonContainer,
	SaveButton,
	MarkAsLostButton,
} from "./styled-components";
import { useGetUserDetailsHooks, useScreenSizeApplier } from "../../hooks";
import Colors from "../../utils/theme/color";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useMarkAsNotVisitedMutation } from "../../../features/api/opportunity-api-slice";

interface CustomizedBottomBarTypes {
	isButtonDisabled?: boolean;
	onPressButton(): void;
	title: string;
	styles?: { bottomTabContainer?: {}; buttonContainer?: {}; buttonStyle?: {} };
	isLink?: boolean;
	leftButtonTitle?: string;
	onClickLeftButton?: () => void;
	rightButtonSupportText?: string;
	saveAsDraft?: () => void;
	isButtonLoading?: boolean;
	isRightButtonRequired?: boolean;
	isLeftButtonRequired?: boolean;
	previousCheckInFound?: boolean;
	showMarkAsNotVisited?: boolean;
	// Secondary right button for additional actions
	secondaryRightButtonTitle?: string;
	onClickSecondaryRightButton?: () => void;
	isSecondaryRightButtonRequired?: boolean;
}

const CustomizedBottomBar = ({
	isButtonDisabled = false,
	onPressButton,
	title,
	styles = {},
	isLink = false,
	leftButtonTitle = "",
	onClickLeftButton,
	rightButtonSupportText = "",
	saveAsDraft = () => { },
	isButtonLoading = false,
	isRightButtonRequired = false,
	isLeftButtonRequired = true,
	previousCheckInFound = true,
	showMarkAsNotVisited = false,  // Secondary button props
	secondaryRightButtonTitle = "",
	onClickSecondaryRightButton = () => { },
	isSecondaryRightButtonRequired = false, }: CustomizedBottomBarTypes) => {
	const { t } = useTranslation();
	const { mergeStyles } = useScreenSizeApplier();
	const { id } = useParams<{ id: string }>();
	const [loadingMark, setLoadingMark] = useState(false);
	const { getUserToken } = useGetUserDetailsHooks();

	const [markAsNotVisited, { isLoading }] = useMarkAsNotVisitedMutation();
	const [confirmOpen, setConfirmOpen] = useState(false);

	const handleConfirmYes = async () => {
		setLoadingMark(true);
		try {
			await handleMarkAsNotVisited(); // uses your existing function
			setConfirmOpen(false);
		} finally {
			setLoadingMark(false);
		}
	};

	const handleConfirmNo = () => setConfirmOpen(false);

	const handleMarkAsNotVisited = async () => {
		if (!id) return toast.error("Invalid Opportunity ID");

		try {
			const result = await markAsNotVisited({
				name: id,
				userToken: getUserToken(),
			}).unwrap();

			toast.success("Opportunity marked as not visited successfully!");
			console.log(result);
		} catch (error: any) {
			toast.error(error?.data?.message || "Something went wrong!");
		}
	};

	return (
		<>
			<div
				className="customized-bottom-container"
				style={styles?.bottomTabContainer}
			>
				<ButtonContainer sx={styles?.buttonContainer}>
					{/* Left side buttons - Mark as Lost and Mark as Not Visited */}
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						{isLeftButtonRequired ? (
							isLink ? (
								<>
									{leftButtonTitle && (
										<MarkAsLostButton
											className="link-container"
											onClick={onClickLeftButton}
										>
											{leftButtonTitle}
										</MarkAsLostButton>
									)}
									{showMarkAsNotVisited && (
										<MarkAsLostButton
											onClick={() => setConfirmOpen(true)}
											disabled={loadingMark}
										>
											{loadingMark ? (
												<CircularProgress
													size="1rem"
													sx={{ color: Colors.primaryColor }}
												/>
											) : (
												"Mark As Not Visited"
											)}
										</MarkAsLostButton>
									)}
								</>
							) : (
								<SaveButton onClick={saveAsDraft} disabled={isButtonLoading}>
									{t("app_common.save_as_draft")}
									{isButtonLoading && (
										<CircularProgress
											size={"1rem"}
											sx={{ ml: 1, color: Colors.primaryColor }}
										/>
									)}
								</SaveButton>
							)
						) : null}
					</div>

					{/* Right side buttons - Booking Proposal and Convert to Proposal */}
					{isRightButtonRequired && (
						<div
							style={mergeStyles({
								styles: { display: "flex", alignItems: "center", gap: "12px" },
								tabletStyles: { display: "flex", flexDirection: "column", gap: "8px" },
								belowLargeStyles: { display: "flex", flexDirection: "column", gap: "8px" },
							})}
						>
							{rightButtonSupportText ? (
								<CustomizedText
									fontSize={16}
									fontFamily={"Lato-Regular"}
									gutterBottom
									sx={{ mr: 2, display: "flex", alignItems: "center" }}
								>
									<InfoRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
									{rightButtonSupportText}
								</CustomizedText>
							) : null}

							{/* Secondary button (booking proposals) */}
							{isSecondaryRightButtonRequired && (
								<MaterialButton
									id="secondary-button"
									title={secondaryRightButtonTitle}
									onClick={onClickSecondaryRightButton}
									fontSize={18}
									size={"small"}
									sx={styles?.buttonStyle}
								/>
							)}

							{/* Main button (convert to proposal) */}
							<MaterialButton
								id="send-otp"
								title={title}
								onClick={onPressButton}
								fontSize={20}
								disabled={isButtonDisabled || isButtonLoading}
								size={"small"}
								sx={styles?.buttonStyle}
								isLoading={isButtonLoading}
							/>
						</div>
					)}
				</ButtonContainer>
			</div>
			<Dialog
				open={confirmOpen}
				onClose={handleConfirmNo}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>Confirm Action</DialogTitle>

				<DialogContent>
					<Typography variant="body2">
						Are you sure you want to mark this opportunity as <b>Not Visited</b>
						?
					</Typography>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleConfirmNo} disabled={loadingMark}>
						No
					</Button>
					<Button
						onClick={handleConfirmYes}
						variant="contained"
						disabled={loadingMark}
					>
						{loadingMark ? (
							<CircularProgress size={18} sx={{ color: "#fff" }} />
						) : (
							"Yes"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CustomizedBottomBar;
